import React, { useRef } from 'react';
import { useAppSelector } from '@redux/store';
import { Link } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useOnClickOutside } from 'hooks/useOnClickOutside/useOnClickOutside';
import OnMenuToggle from 'hooks/MenuToggle/OnMenuToggle';
import { HomeRoutes } from 'Routing/RouteConstants';
import { authTypes } from 'utils/Auth';
import { resetAuthorizedUserState } from 'utils/Auth/handleDispatchToLogout';
import { IUserTenants } from '@redux/Users/users.interface';
import cn from 'utils/cn';

const useStyles = makeStyles()({
  root: {
    fontSize: 14,
    lineHeight: '16px',
    padding: 0,
  },
  selected: {
    backgroundColor: 'var(--selected-row-blue)',
    color: 'white',
  },
});

function TenantListMenu() {
  const currentUser = useAppSelector((state) => state.users.currentUser);
  const authType = useAppSelector((state) => state.userInfo.authType);

  const { classes: menuItemClasses } = useStyles();

  const [isOpen, toggleOpen] = OnMenuToggle();
  const anchorEl = useRef<HTMLDivElement | null>(null);

  const userName = currentUser?.userName || '';

  const handleClose = () => toggleOpen(false);

  useOnClickOutside(anchorEl, handleClose);

  return (
    <div ref={anchorEl} data-testid="tenants-list-menu">
      <span
        className={cn(
          'tw:text-base/5 tw:font-light tw:mt-4 tw:flex tw:items-center tw:h-[40px]',
          authType !== authTypes.eoauth ? 'tw:cursor-pointer' : ''
        )}
        onClick={() => {
          if (authType !== authTypes.eoauth) {
            toggleOpen(true);
          }
        }}
      >
        <span
          className={cn(
            'tw:inline-block tw:truncate',
            authType !== authTypes.eoauth ? 'tw:max-w-[120px]' : 'tw:max-w-[150px]'
          )}
          title={currentUser.currentTenantName}
        >
          {currentUser.currentTenantName}
        </span>
        {authType !== authTypes.eoauth && (
          <ExpandMoreIcon className={isOpen ? 'tw:rotate-180' : ''} />
        )}
      </span>

      {authType !== authTypes.eoauth ? (
        <Menu
          id="tenant-list-menu"
          anchorEl={anchorEl.current}
          keepMounted
          open={isOpen}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          onClick={handleClose}
          style={{ zIndex: 12000 }}
        >
          {(currentUser?.tenants || []).map((item: IUserTenants) => (
            <MenuItem
              key={item.id}
              classes={{
                root: menuItemClasses.root,
                selected: menuItemClasses.selected,
              }}
              dense
              selected={currentUser.tenantId === item.id}
              disabled={currentUser.tenantId === item.id}
            >
              <Link
                to={`/${HomeRoutes.login}?userName=${encodeURIComponent(userName)}&tenantId=${
                  item.id
                }`}
                className={cn(
                  'cod-gray tw:block tw:px-3 tw:py-2 tw:no-underline tw:w-full',
                  currentUser.tenantId === item.id ? 'tw:cursor-not-allowed' : 'tw:cursor-pointer'
                )}
                onClick={resetAuthorizedUserState}
              >
                {item.name}
              </Link>
            </MenuItem>
          ))}
        </Menu>
      ) : (
        <a
          href="/"
          className="tw:text-primary-100 tw:text-sm tw:leading-4"
          onClick={resetAuthorizedUserState}
        >
          Change tenant
        </a>
      )}
    </div>
  );
}

export default TenantListMenu;
